<template>
  <div class="viz-root">
    <!-- KPI tiles -->
    <v-row dense>
      <v-col cols="6" md="3" v-for="tile in kpiTiles" :key="tile.label">
        <v-card outlined class="pa-4">
          <div class="viz-secondary text-sm mb-1">{{ tile.label }}</div>
          <div class="viz-hero">{{ tile.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Daily traffic line chart -->
    <v-card outlined class="pa-4 mt-3" v-if="daily.length">
      <div class="d-flex align-center mb-2">
        <h3 class="font-semibold">{{ trendTitle }}</h3>
        <v-spacer></v-spacer>
        <span class="viz-legend"><span class="viz-swatch" style="background:#2a78d6"></span>Total views</span>
        <span class="viz-legend ml-4"><span class="viz-swatch" style="background:#eb6834"></span>Unique visitors</span>
      </div>
      <div style="position:relative" @mouseleave="hoverIndex = null">
        <svg ref="trend" viewBox="0 0 800 260" width="100%" style="display:block" @mousemove="onTrendHover">
          <g v-for="tick in yTicks" :key="'t' + tick.y">
            <line x1="44" :y1="tick.y" x2="792" :y2="tick.y" stroke="#e1e0d9" stroke-width="1" />
            <text x="38" :y="tick.y + 4" font-size="11" fill="#898781" text-anchor="end">{{ tick.label }}</text>
          </g>
          <text v-for="tick in xTicks" :key="'x' + tick.x" :x="tick.x" y="252" font-size="11" fill="#898781" :text-anchor="tick.anchor">{{ tick.label }}</text>
          <polyline :points="trendPoints('views')" fill="none" stroke="#2a78d6" stroke-width="2" stroke-linejoin="round" />
          <polyline :points="trendPoints('unique_visitors')" fill="none" stroke="#eb6834" stroke-width="2" stroke-linejoin="round" />
          <!-- Point markers. A polyline of one point draws nothing, so a
               single-day range (Today, the default on both pages) would
               render an empty chart without these. Hidden on long ranges,
               where they would crowd the line. -->
          <g v-if="showPointMarkers">
            <circle v-for="(d, i) in trendSeries" :key="'pv' + i" :cx="trendX(i)" :cy="trendY(d.views)" r="3" fill="#2a78d6" />
            <circle v-for="(d, i) in trendSeries" :key="'pu' + i" :cx="trendX(i)" :cy="trendY(d.unique_visitors)" r="3" fill="#eb6834" />
          </g>
          <g v-if="hoverIndex !== null">
            <line :x1="trendX(hoverIndex)" y1="12" :x2="trendX(hoverIndex)" y2="232" stroke="#c3c2b7" stroke-width="1" />
            <circle :cx="trendX(hoverIndex)" :cy="trendY(trendSeries[hoverIndex].views)" r="4" fill="#2a78d6" stroke="#fcfcfb" stroke-width="2" />
            <circle :cx="trendX(hoverIndex)" :cy="trendY(trendSeries[hoverIndex].unique_visitors)" r="4" fill="#eb6834" stroke="#fcfcfb" stroke-width="2" />
          </g>
        </svg>
        <div v-if="hoverIndex !== null" class="viz-tooltip" :style="tooltipStyle">
          <div class="font-semibold mb-1">{{ trendSeries[hoverIndex].label }}</div>
          <div><span class="viz-swatch" style="background:#2a78d6"></span>Total views: <strong>{{ fmt(trendSeries[hoverIndex].views) }}</strong></div>
          <div><span class="viz-swatch" style="background:#eb6834"></span>Unique visitors: <strong>{{ fmt(trendSeries[hoverIndex].unique_visitors) }}</strong></div>
        </div>
      </div>
    </v-card>

    <!-- Breakdowns row 1: traffic sources -->
    <v-row dense class="mt-1">
      <v-col cols="12">
        <v-card outlined class="pa-4" style="height:100%">
          <h3 class="font-semibold mb-3">Traffic Sources</h3>
          <div v-for="row in referrerRows" :key="row.label" class="mb-3">
            <div class="d-flex justify-space-between text-sm mb-1">
              <span class="viz-secondary">{{ row.label }}</span>
              <span>{{ fmt(row.views) }} <span class="viz-muted">({{ row.percent }}%)</span></span>
            </div>
            <div class="viz-track"><div class="viz-bar" :style="{ width: Math.max(row.percent, 1) + '%' }"></div></div>
          </div>
          <div v-if="!referrerRows.length" class="viz-muted text-sm">No data for this range.</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Breakdowns row 2: content types / busiest hours -->
    <v-row dense class="mt-1">
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4" style="height:100%">
          <h3 class="font-semibold mb-3">What People View</h3>
          <div v-for="row in contentRows" :key="row.label" class="mb-3">
            <div class="d-flex justify-space-between text-sm mb-1">
              <span class="viz-secondary">{{ row.label }}</span>
              <span>{{ fmt(row.views) }} <span class="viz-muted">({{ row.percent }}%)</span></span>
            </div>
            <div class="viz-track"><div class="viz-bar" :style="{ width: Math.max(row.percent, 1) + '%' }"></div></div>
          </div>
          <v-divider class="my-3"></v-divider>
          <div class="d-flex justify-space-between text-sm">
            <span class="viz-secondary">Views by logged-in users</span>
            <span>{{ fmt(data.loggedInViews || 0) }} <span class="viz-muted">({{ loggedInPercent }}%)</span></span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="8">
        <v-card outlined class="pa-4" style="height:100%">
          <h3 class="font-semibold mb-3">Busiest Hours</h3>
          <div style="position:relative" @mouseleave="hourHover = null">
            <svg ref="hours" viewBox="0 0 800 240" width="100%" style="display:block" @mousemove="onHourHover">
              <g v-for="tick in hourTicks" :key="'ht' + tick.y">
                <line x1="44" :y1="tick.y" x2="792" :y2="tick.y" stroke="#e1e0d9" stroke-width="1" />
                <text x="38" :y="tick.y + 4" font-size="13" fill="#898781" text-anchor="end">{{ tick.label }}</text>
              </g>
              <g v-for="h in 24" :key="'hb' + h">
                <rect
                  :x="hourX(h - 1)"
                  :y="200 - hourHeight(h - 1)"
                  :width="hourBarWidth"
                  :height="hourHeight(h - 1)"
                  :fill="hourHover === h - 1 ? '#1c5cab' : '#2a78d6'"
                  rx="4"
                />
              </g>
              <line x1="44" y1="200" x2="792" y2="200" stroke="#c3c2b7" stroke-width="1" />
              <text v-for="h in [0, 6, 12, 18, 23]" :key="'hl' + h" :x="hourX(h) + hourBarWidth / 2" y="226" font-size="13" fill="#898781" text-anchor="middle">{{ pad(h) }}</text>
            </svg>
            <div v-if="hourHover !== null" class="viz-tooltip" :style="hourTooltipStyle">
              <div class="font-semibold mb-1">{{ pad(hourHover) }}:00 – {{ pad((hourHover + 1) % 24) }}:00</div>
              <div>Views: <strong>{{ fmt(hourViews(hourHover)) }}</strong> <span class="viz-muted">({{ hourPercent(hourHover) }}% of traffic)</span></div>
              <div class="viz-muted">≈ {{ fmt(hourPerDay(hourHover)) }} per day</div>
            </div>
          </div>
          <div class="viz-muted text-sm mt-2" v-if="peakHour !== null">
            Peak hour: <strong class="viz-secondary">{{ pad(peakHour) }}:00–{{ pad((peakHour + 1) % 24) }}:00</strong>
            ({{ fmt(hourViews(peakHour)) }} views) · Quietest: {{ pad(quietHour) }}:00 ({{ fmt(hourViews(quietHour)) }})
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Top pages -->
    <v-card outlined class="pa-4 mt-3" v-if="(data.topPages || []).length">
      <h3 class="font-semibold mb-3">Top Pages</h3>
      <div v-for="page in data.topPages" :key="page.path" class="viz-pagerow">
        <div class="viz-pagepath">
          <a :href="page.path" target="_blank" rel="noopener noreferrer">{{ page.path }}</a>
          <span v-if="pathNote(page.path)" class="viz-muted ml-2">({{ pathNote(page.path) }})</span>
        </div>
        <div class="viz-track viz-pagetrack">
          <div class="viz-bar" :style="{ width: pagePercent(page.views) + '%' }"></div>
        </div>
        <div class="viz-count">{{ fmt(page.views) }}</div>
        <div class="viz-count viz-muted">{{ fmt(page.unique_visitors) }} unique</div>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'TrafficSummaryPanels',
  props: {
    // The /admin/analytics/traffic-summary response object.
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      hoverIndex: null,
      tooltipPos: { x: 0, y: 0 },
      hourHover: null,
      hourTooltipPos: { x: 0, y: 0 },
    };
  },
  computed: {
    daily() {
      return this.data.daily || [];
    },
    totals() {
      return this.data.totals || { views: 0, uniqueIps: 0, uniqueUrls: 0 };
    },
    kpiTiles() {
      const days = Math.max(this.daily.length, 1);
      return [
        { label: 'Total Views', value: this.fmt(this.totals.views) },
        { label: 'Unique Visitors', value: this.fmt(this.totals.uniqueIps) },
        { label: 'Unique URLs', value: this.fmt(this.totals.uniqueUrls) },
        { label: 'Avg Views / Day', value: this.fmt(Math.round(this.totals.views / days)) },
      ];
    },
    maxDailyViews() {
      return Math.max(1, ...this.trendSeries.map(d => Number(d.views)));
    },
    // A "Daily Traffic" line cannot say anything useful about a single day
    // — it is one point. When the range covers one day, plot that day's
    // hours instead, which is what the reader actually wants to see.
    isSingleDay() {
      return this.daily.length === 1;
    },
    monthlyBuckets() {
      return this.data.monthly || [];
    },
    // The server sends month buckets only for ranges too long to read day
    // by day, so their presence is the signal to plot months.
    useMonthly() {
      return !this.isSingleDay && this.monthlyBuckets.length > 0;
    },
    trendTitle() {
      if (this.isSingleDay) return 'Hourly Traffic';
      return this.useMonthly ? 'Monthly Traffic' : 'Daily Traffic';
    },
    trendSeries() {
      if (this.useMonthly) {
        const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return this.monthlyBuckets.map(m => {
          const [year, month] = String(m.month).split('-');
          return {
            label: `${names[Number(month) - 1]} ${year}`,
            views: Number(m.views),
            unique_visitors: Number(m.unique_visitors),
          };
        });
      }
      if (!this.isSingleDay) {
        return this.daily.map(d => ({
          label: d.date,
          views: Number(d.views),
          unique_visitors: Number(d.unique_visitors),
        }));
      }
      if (!this.hourly.length) return [];
      // Fill gaps so the axis reads as a real timeline: an hour with no
      // traffic must show as a dip rather than be skipped. Stop at the last
      // hour that has data instead of padding to 23, which would draw the
      // rest of today as a flat zero line.
      const byHour = {};
      this.hourly.forEach(h => { byHour[Number(h.hour)] = h; });
      const lastHour = Math.max(...this.hourly.map(h => Number(h.hour)));
      const out = [];
      for (let hour = 0; hour <= lastHour; hour++) {
        const row = byHour[hour];
        out.push({
          label: this.pad(hour) + ':00',
          views: row ? Number(row.views) : 0,
          unique_visitors: row ? Number(row.unique_visitors || 0) : 0,
        });
      }
      return out;
    },
    showPointMarkers() {
      // Roughly a month or less: few enough points to mark individually.
      return this.trendSeries.length <= 31;
    },
    yTicks() {
      const max = this.maxDailyViews;
      return [0, 0.5, 1].map(f => ({
        y: this.trendY(max * f),
        label: this.shortNumber(Math.round(max * f)),
      }));
    },
    xTicks() {
      const series = this.trendSeries;
      if (!series.length) return [];
      const ticks = [{ x: 44, label: series[0].label, anchor: 'start' }];
      if (series.length > 2) {
        const mid = Math.floor((series.length - 1) / 2);
        ticks.push({ x: this.trendX(mid), label: series[mid].label, anchor: 'middle' });
      }
      if (series.length > 1) {
        ticks.push({ x: 792, label: series[series.length - 1].label, anchor: 'end' });
      }
      return ticks;
    },
    referrerRows() {
      return this.toShareRows(this.data.referrers || [], 'source');
    },
    contentRows() {
      return this.toShareRows(this.data.contentTypes || [], 'content');
    },
    loggedInPercent() {
      const total = Number(this.totals.views);
      return total ? Math.round((Number(this.data.loggedInViews || 0) / total) * 100) : 0;
    },
    hourly() {
      return this.data.hourly || [];
    },
    maxHourViews() {
      return Math.max(1, ...this.hourly.map(h => Number(h.views)));
    },
    totalHourViews() {
      return this.hourly.reduce((sum, h) => sum + Number(h.views), 0);
    },
    hourTicks() {
      const max = this.maxHourViews;
      return [0.5, 1].map(f => ({
        y: 200 - f * 176,
        label: this.shortNumber(Math.round(max * f)),
      }));
    },
    hourBarWidth() {
      return (748 / 24) - 4;
    },
    peakHour() {
      if (!this.hourly.length) return null;
      const peak = this.hourly.reduce((a, b) => (Number(b.views) > Number(a.views) ? b : a));
      return Number(peak.hour);
    },
    quietHour() {
      if (!this.hourly.length) return null;
      const quiet = this.hourly.reduce((a, b) => (Number(b.views) < Number(a.views) ? b : a));
      return Number(quiet.hour);
    },
    tooltipStyle() {
      return { left: this.tooltipPos.x + 'px', top: this.tooltipPos.y + 'px' };
    },
    hourTooltipStyle() {
      return { left: this.hourTooltipPos.x + 'px', top: this.hourTooltipPos.y + 'px' };
    },
  },
  methods: {
    fmt(value) {
      return Number(value || 0).toLocaleString();
    },
    pad(hour) {
      return String(hour).padStart(2, '0');
    },
    // Trend chart geometry: plot area x 44→792, y 12→232 in an 800x260 viewBox.
    trendX(index) {
      const n = this.trendSeries.length;
      if (n <= 1) return 418;
      return 44 + (index / (n - 1)) * 748;
    },
    trendY(value) {
      return 232 - (Number(value) / this.maxDailyViews) * 220;
    },
    trendPoints(key) {
      return this.trendSeries.map((d, i) => `${this.trendX(i)},${this.trendY(d[key])}`).join(' ');
    },
    onTrendHover(event) {
      const svg = this.$refs.trend;
      if (!svg || !this.trendSeries.length) return;
      const rect = svg.getBoundingClientRect();
      const viewX = ((event.clientX - rect.left) / rect.width) * 800;
      const n = this.trendSeries.length;
      const index = n <= 1 ? 0 : Math.round(((viewX - 44) / 748) * (n - 1));
      this.hoverIndex = Math.min(Math.max(index, 0), n - 1);
      const px = this.trendX(this.hoverIndex) * (rect.width / 800);
      this.tooltipPos = { x: px > rect.width - 200 ? px - 190 : px + 14, y: 20 };
    },
    // Hour chart geometry: plot area x 44→792, bars bottom at y 200 in an 800x240 viewBox.
    hourX(hour) {
      return 44 + hour * (748 / 24) + 2;
    },
    hourViews(hour) {
      const row = this.hourly.find(h => Number(h.hour) === hour);
      return row ? Number(row.views) : 0;
    },
    hourHeight(hour) {
      return Math.round((this.hourViews(hour) / this.maxHourViews) * 176);
    },
    hourPercent(hour) {
      return this.totalHourViews ? Math.round((this.hourViews(hour) / this.totalHourViews) * 100) : 0;
    },
    hourPerDay(hour) {
      const days = Math.max(this.daily.length, 1);
      return Math.round(this.hourViews(hour) / days);
    },
    onHourHover(event) {
      const svg = this.$refs.hours;
      if (!svg || !this.hourly.length) return;
      const rect = svg.getBoundingClientRect();
      const viewX = ((event.clientX - rect.left) / rect.width) * 800;
      const hour = Math.floor((viewX - 44) / (748 / 24));
      this.hourHover = Math.min(Math.max(hour, 0), 23);
      const px = (this.hourX(this.hourHover) + this.hourBarWidth / 2) * (rect.width / 800);
      this.hourTooltipPos = { x: px > rect.width - 190 ? px - 180 : px + 10, y: 10 };
    },
    shortNumber(value) {
      if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
      if (value >= 1000) return (value / 1000).toFixed(value >= 10000 ? 0 : 1) + 'k';
      return String(value);
    },
    pagePercent(views) {
      const max = Math.max(1, ...(this.data.topPages || []).map(p => Number(p.views)));
      return Math.max(Math.round((Number(views) / max) * 100), 1);
    },
    toShareRows(items, key) {
      const total = items.reduce((sum, i) => sum + Number(i.views), 0);
      return items.map(i => ({
        label: i[key],
        views: i.views,
        percent: total ? Math.round((Number(i.views) / total) * 100) : 0,
      }));
    },
    pathNote(path) {
      if (path === '/') return 'Homepage';
      if (path === '/rss' || path.startsWith('/rss/')) return 'RSS feed';
      return '';
    },
  },
};
</script>

<style scoped>
/* Analytics dashboard chrome — reference dataviz palette, light mode */
.viz-root {
  --viz-ink: #0b0b0b;
  --viz-secondary: #52514e;
  --viz-muted: #898781;
  --viz-track: #f0efec;
  --viz-series-1: #2a78d6;
  color: var(--viz-ink);
}
.viz-secondary { color: var(--viz-secondary); }
.viz-muted { color: var(--viz-muted); }
.viz-hero {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.2;
}
.viz-legend {
  font-size: 0.8rem;
  color: var(--viz-secondary);
  display: inline-flex;
  align-items: center;
}
.viz-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 6px;
}
.viz-track {
  background: var(--viz-track);
  border-radius: 4px;
  height: 10px;
  overflow: hidden;
}
.viz-bar {
  background: var(--viz-series-1);
  border-radius: 0 4px 4px 0;
  height: 100%;
}
.viz-tooltip {
  position: absolute;
  background: #fff;
  border: 1px solid rgba(11, 11, 11, 0.1);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(11, 11, 11, 0.12);
  padding: 8px 12px;
  font-size: 0.8rem;
  pointer-events: none;
  white-space: nowrap;
  z-index: 5;
}
.viz-tooltip .viz-swatch { width: 8px; height: 8px; }
.viz-pagerow {
  display: grid;
  grid-template-columns: minmax(180px, 2fr) 3fr 90px 110px;
  gap: 12px;
  align-items: center;
  padding: 6px 0;
}
.viz-pagepath {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
}
.viz-pagetrack { height: 14px; }
.viz-count {
  text-align: right;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
}
@media (max-width: 700px) {
  .viz-pagerow { grid-template-columns: 1fr 80px; }
  .viz-pagetrack, .viz-count.viz-muted { display: none; }
}
</style>
